// pages/LoginPage.tsx

import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Typography, Select, Divider, Radio, Space } from 'antd';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Company } from '../types/company';
import { getRoutePath } from '../config';

const { Title, Text } = Typography;
const { Option } = Select;

interface LoginFormValues {
  loginid: string;
  password: string;
}

interface CompanySelectFormValues {
  companyid: string;
}

// ログインのステップ
enum LoginStep {
  Credentials = 0,
  CompanySelect = 1
}

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<LoginStep>(LoginStep.Credentials);
  const [loginForm] = Form.useForm<LoginFormValues>();
  const [companyForm] = Form.useForm<CompanySelectFormValues>();
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [loginCredentials, setLoginCredentials] = useState<LoginFormValues | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [lastLoginAttempt, setLastLoginAttempt] = useState<LoginFormValues | null>(null);
  
  const navigate = useNavigate();
  const { getCompanyList, login, availableCompanies, isAuthenticated, hasSelectedCompany } = useAuth();

  // 既に認証済みで企業選択も済んでいる場合はメニュー画面へリダイレクト
  useEffect(() => {
    if (isAuthenticated && hasSelectedCompany) {
      navigate(getRoutePath('/menu'));
    } else if (isAuthenticated && !hasSelectedCompany && availableCompanies.length > 0) {
      // 認証済みだが企業選択がまだの場合
      setCurrentStep(LoginStep.CompanySelect);
    }
  }, [isAuthenticated, hasSelectedCompany, availableCompanies, navigate]);

  // 自動リトライの処理
  useEffect(() => {
    // 前回のログイン試行があり、エラーメッセージが特定のものでリトライカウントが1未満の場合
    if (lastLoginAttempt && 
        errorMessage === 'ログイン可能な企業がありません' && 
        retryCount < 1) {
      // 自動的に再試行
      const retryLogin = async () => {
        setRetryCount(prev => prev + 1);
        await onLoginFinish(lastLoginAttempt);
      };
      
      // 少し遅延を入れて再試行
      const timeoutId = setTimeout(() => {
        retryLogin();
      }, 800);
      
      return () => clearTimeout(timeoutId);
    }
  }, [lastLoginAttempt, errorMessage, retryCount]);

  const onLoginFinish = async (values: LoginFormValues): Promise<void> => {
    try {
      setLoading(true);
      setErrorMessage(null); // エラーメッセージをクリア
      
      // 今回のログイン試行情報を保存
      setLastLoginAttempt(values);
      
      // 前後の半角・全角スペースを削除
      const loginid = values.loginid.trim();
      const password = values.password.trim();

      // 認証情報を保存（企業選択で使用するため）
      setLoginCredentials({ loginid, password });
      
      // 企業一覧を取得
      const companiesResult = await getCompanyList(loginid, password);
      
      if (companiesResult.success) {
        // 修正：ここで availableCompanies の代わりに companiesResult.companyList を使用
        const companyList = companiesResult.companyList || [];
        
        if (companyList.length === 1) {
          // 企業が1つだけの場合は自動的にログイン
          const companyid = companyList[0].companyid || '';
          
          // companyidが空文字の場合はエラーメッセージを表示
          if (!companyid) {
            setErrorMessage('有効な企業IDが見つかりません');
            setLoading(false);
            return;
          }
          
          const loginResult = await login(loginid, password, companyid);
          
          if (loginResult.success) {
            message.success('ログインに成功しました');
            navigate(getRoutePath('/menu'));
          } else {
            setErrorMessage(loginResult.message || 'ログインに失敗しました');
          }
        } else if (companyList.length > 1) {
          // 複数の企業がある場合は選択画面へ
          message.success('ログイン可能な企業を取得しました');
          setCurrentStep(LoginStep.CompanySelect);
        } else {
          setErrorMessage('ログイン可能な企業がありません');
          
          // リトライカウントが少ない場合はログに記録のみ（useEffectでリトライする）
          if (retryCount < 1) {
            }
        }
      } else {
        // エラーメッセージを画面に表示
        setErrorMessage(companiesResult.message || 'ユーザー名またはパスワードが正しくありません');
      }
    } catch (error) {
      setErrorMessage('ログイン中にエラーが発生しました。ネットワーク接続を確認してください。');
    } finally {
      setLoading(false);
    }
  };

  const onCompanySelectFinish = async (values: CompanySelectFormValues): Promise<void> => {
    try {
      setErrorMessage(null); // エラーメッセージをクリア
      
      if (!loginCredentials) {
        setErrorMessage('ログイン情報が失われました。再度ログインしてください。');
        setCurrentStep(LoginStep.Credentials);
        return;
      }
      
      setLoading(true);
      const { companyid } = values;
      
      // companyidが存在することを確認
      if (!companyid) {
        setErrorMessage('企業を選択してください');
        setLoading(false);
        return;
      }
      
      const { loginid, password } = loginCredentials;
      
      const result = await login(loginid, password, companyid);
      
      if (result.success) {
          message.success('企業選択に成功しました');
        navigate(getRoutePath('/menu'));
      } else {
          setErrorMessage(result.message || '企業選択に失敗しました');
      }
    } catch (error) {
      setErrorMessage('企業選択中にエラーが発生しました。ネットワーク接続を確認してください。');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySelect = (companyid: string | undefined) => {
    // companyidがundefinedの場合は処理しない
    if (!companyid) {
      return;
    }
    
    setSelectedCompany(companyid);
    companyForm.setFieldsValue({ companyid });
  };

  // 手動再試行処理
  const handleRetry = () => {
    if (lastLoginAttempt) {
      setRetryCount(0); // リトライカウントをリセット
      onLoginFinish(lastLoginAttempt);
    }
  };

  // 企業選択フォームのレンダリング
  const renderCompanySelectForm = () => (
    <Form<CompanySelectFormValues>
      form={companyForm}
      name="company-select"
      onFinish={onCompanySelectFinish}
      layout="vertical"
    >
      {/* エラーメッセージ表示エリア */}
      {errorMessage && (
        <div 
          style={{ 
            backgroundColor: '#ffebee', 
            color: '#d32f2f', 
            padding: '10px 15px', 
            borderRadius: '4px', 
            marginBottom: '16px',
            fontSize: '14px'
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>エラー</div>
          {errorMessage}
          <div style={{ marginTop: '8px' }}>
            <Button size="small" onClick={() => setCurrentStep(LoginStep.Credentials)}>
              ログイン画面に戻る
            </Button>
          </div>
        </div>
      )}

      <Form.Item
        name="companyid"
        /*label="アクセスする企業を選択してください"
        rules={[{ required: true, message: '企業を選択してください' }]}*/
      >
        <Select
          showSearch
          size="large"
          placeholder="企業名で検索して選択してください"
          optionFilterProp="children"
          onChange={(value: string) => handleCompanySelect(value)}
          style={{ width: '100%' }}
        >
          {availableCompanies.map((company: Company) => {
            const companyId = company.companyid || '';
            return (
              <Option key={companyId} value={companyId}>
                {company.companyname || ''}
              </Option>
            );
          })}
        </Select>
      </Form.Item>

      <Form.Item style={{ marginTop: '24px' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button 
            onClick={() => setCurrentStep(LoginStep.Credentials)}
          >
            戻る
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            disabled={!selectedCompany}
          >
            選択して続ける
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );

  // 認証情報入力フォームのレンダリング
  const renderCredentialsForm = () => (
    <Form<LoginFormValues>
      form={loginForm}
      name="login"
      initialValues={{ remember: true }}
      onFinish={onLoginFinish}
    >
      {/* エラーメッセージ表示エリア */}
      {errorMessage && (
        <div 
          style={{ 
            backgroundColor: '#ffebee', 
            color: '#d32f2f', 
            padding: '10px 15px', 
            borderRadius: '4px', 
            marginBottom: '16px',
            fontSize: '14px'
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>エラー</div>
          {errorMessage}
          {errorMessage === 'ログイン可能な企業がありません' && retryCount >= 1 && (
            <div style={{ marginTop: '8px' }}>
              <Button size="small" onClick={handleRetry}>
                再試行
              </Button>
            </div>
          )}
        </div>
      )}

      <Form.Item
        name="loginid"
        rules={[{ required: true, message: 'ログインIDを入力してください' }]}
      >
        <Input 
          prefix={<UserOutlined />} 
          placeholder="ログインID" 
          size="large" 
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[{ required: true, message: 'パスワードを入力してください' }]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="パスワード"
          size="large"
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          style={{ width: '100%' }}
          size="large"
        >
          ログイン
        </Button>
      </Form.Item>

      <div style={{ textAlign: 'center' }}>
        <a onClick={() => setForgotPasswordVisible(true)} style={{ cursor: 'pointer' }}>
          パスワードを忘れた方へ
        </a>
      </div>

      <ForgotPasswordModal
        open={forgotPasswordVisible}
        onClose={() => setForgotPasswordVisible(false)}
      />
    </Form>
  );

  // 現在のステップに応じた内容を表示
  const renderCurrentStepContent = () => {
    switch (currentStep) {
      case LoginStep.CompanySelect:
        return (
          <>
            <div style={{ marginBottom: 24, textAlign: 'center' }}>
              <Title level={4}>企業選択</Title>
              <Text type="secondary">アクセスする企業を選択してください</Text>
            </div>
            {renderCompanySelectForm()}
          </>
        );
      case LoginStep.Credentials:
      default:
        return renderCredentialsForm();
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: '#f0f2f5'
    }}>
      <Card style={{ width: 400, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24, padding: '16px' }}>
          <img 
            src="./logo.png" 
            alt="UP-TONE" 
            style={{ 
              height: '45px',
              width: 'auto',
              objectFit: 'contain'
            }} 
          />
        </div>
        
        {renderCurrentStepContent()}
      </Card>
    </div>
  );
};

export default LoginPage;